import {
  createDecisionRecommendation,
  DECISION_ENGINES,
  DECISION_SCOPES,
  DECISION_TYPES,
} from "../models";

export const EXECUTIVE_RECOMMENDATION_STATUS = Object.freeze({
  AVAILABLE: "AVAILABLE",
  UNAVAILABLE: "UNAVAILABLE",
  INCOMPLETE_EVIDENCE: "INCOMPLETE_EVIDENCE",
  POLICY_UNAVAILABLE: "POLICY_UNAVAILABLE",
  INVALID_CONTRACT: "INVALID_CONTRACT",
});

const TYPE_MAP = Object.freeze({
  BASELINE: DECISION_TYPES.RETAIN,
  MITIGATION_ONLY: DECISION_TYPES.MITIGATE,
  TRANSFER_ONLY: DECISION_TYPES.TRANSFER,
  RETENTION_ONLY: DECISION_TYPES.RETAIN,
  MITIGATION_TRANSFER: DECISION_TYPES.MITIGATE,
  MITIGATION_RETENTION: DECISION_TYPES.MITIGATE,
  TRANSFER_RETENTION: DECISION_TYPES.TRANSFER,
  MITIGATION_TRANSFER_RETENTION: DECISION_TYPES.MITIGATE,
  PORTFOLIO: DECISION_TYPES.REALLOCATE_CAPITAL,
});

export const mapBundleTypeToDecisionType = (bundleType) =>
  TYPE_MAP[bundleType] ?? null;

/** Losslessly normalize the Board projection; never select or calculate here. */
export function executiveRecommendationAdapter(executiveRecommendation) {
  if (executiveRecommendation == null) return null;

  const identity = executiveRecommendation.identity;
  if (!identity?.recommendationId || !identity?.bundleType ||
      !executiveRecommendation.headline ||
      !executiveRecommendation.boardRationale) return null;

  const decisionType = mapBundleTypeToDecisionType(identity.bundleType);
  if (!decisionType) return null;

  const economics = executiveRecommendation.economicSummary ?? null;
  const governance = executiveRecommendation.governanceSummary ?? null;
  const support = executiveRecommendation.supportingEvidence ?? null;
  const alternatives = executiveRecommendation.alternativesConsidered ?? [];
  const comparisonDiagnostics = support?.comparisonDiagnostics ?? null;
  const selected = alternatives.find((alternative) => alternative?.selected === true);
  // `identity.bundleId` can name the aggregate portfolio.  The selected
  // candidate is separately and authoritatively identified by whySelected.
  const selectedCandidateIds = identity.selectedCandidateIds ??
    (identity.selectedCandidateId != null ? [identity.selectedCandidateId] : []);
  const selectedBundleIds = identity.selectedBundleIds ?? identity.bundleIds ??
    (identity.selectedBundleId != null ? [identity.selectedBundleId] : []);
  const mechanismSequence = selected?.bundle?.mechanismSequence ??
    (identity.bundleType === "PORTFOLIO"
      ? alternatives.filter((item) => item?.selected === true)
        .map((item) => item?.bundle?.mechanismSequence)
      : null);

  return createDecisionRecommendation({
    id: identity.recommendationId,
    source: "capital_allocation",
    engine: DECISION_ENGINES.PORTFOLIO,
    scope: comparisonDiagnostics?.comparisonScope === "SCENARIO"
      ? DECISION_SCOPES.SCENARIO : DECISION_SCOPES.PORTFOLIO,
    decisionType,
    title: executiveRecommendation.headline,
    rationale: executiveRecommendation.boardRationale,
    priority: executiveRecommendation.priority ?? null,
    financialImpact: economics,
    evidence: {
      whySelected: executiveRecommendation.whySelected ?? [],
      alternatives,
      alternativesRejected: executiveRecommendation.alternativesRejected ?? [],
      supportingEvidence: support,
    },
    metadata: {
      mode: "CAPITAL_ALLOCATION",
      authoritativeRecommendationId: identity.recommendationId,
      selectedPortfolioId: identity.selectedPortfolioId ?? identity.portfolioId ?? null,
      selectedBundleId: identity.selectedBundleId ?? identity.bundleId ?? null,
      selectedBundleIds,
      selectedCandidateId: identity.selectedCandidateId ?? selectedCandidateIds[0] ?? null,
      selectedCandidateIds,
      selectionSource: identity.selectionSource ?? null,
      bundleType: identity.bundleType,
      mechanismSequence,
      selectionReasonCode: governance?.selectionReason ?? null,
      comparisonHorizon: identity.comparisonHorizon ?? null,
      capitalBasis: identity.capitalBasis ?? null,
      confidence: executiveRecommendation.boardConfidence ?? null,
      policyReference: {
        id: identity.policyId ?? null,
        version: identity.policyVersion ?? null,
      },
      recommendationStatus: EXECUTIVE_RECOMMENDATION_STATUS.AVAILABLE,
      governance,
      supportingEvidenceReferences: support == null ? null : {
        candidateComparisonReference: support.candidateComparisonReference ?? null,
        selectedBundleReference: support.selectedBundleReference ?? null,
      },
      comparisonScope: comparisonDiagnostics?.comparisonScope ??
        (identity.bundleType === "PORTFOLIO" ? "PORTFOLIO" : "SCENARIO"),
      comparisonDiagnostics,
    },
  });
}
